import React, { useState, useRef, useEffect } from 'react';
import { HeartIcon } from './Icons';
import MenuDrawer from './MenuDrawer';
import AuthModal from './AuthModal';
import Footer from './Footer';
import BookingDrawer from './BookingDrawer';
import { saveReview, uploadImage, subscribeReviews, triggerStockLimitAlert } from '../utils/firebaseService';
import Header from './Header';

// ─── Star rating helper ───────────────────────────────────────────────────────
const Stars = ({ rating = 5, size = 'sm' }) => {
  const sz = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <svg key={i} className={`${sz} ${i <= rating ? 'text-amber-500' : 'text-[#584738]/20'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

// ─── Rating bar ──────────────────────────────────────────────────────────────
const RatingBar = ({ label, pct }) => (
  <div className="flex items-center gap-3 text-[11px] font-sans text-[#584738]/70">
    <span className="w-12 text-right shrink-0">{label}</span>
    <div className="flex-1 h-1.5 bg-[#584738]/10 rounded-full overflow-hidden">
      <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
    </div>
  </div>
);

// ─── Review card ─────────────────────────────────────────────────────────────
const ReviewCard = ({ name, date, text, rating = 5, images = [], onImageClick }) => (
  <div className="border-b border-[#584738]/10 py-6">
    <div className="flex items-start justify-between gap-4 mb-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#584738]/15 flex items-center justify-center text-[#584738] font-serif font-semibold text-sm shrink-0">
          {name[0]}
        </div>
        <div>
          <p className="text-sm font-sans font-semibold text-[#584738]">{name}</p>
          <p className="text-[10px] font-sans text-[#584738]/50 tracking-wider">Verified Buyer</p>
        </div>
      </div>
      <span className="text-[11px] text-[#584738]/45 font-sans shrink-0">{date}</span>
    </div>
    <p className="text-[13px] font-sans text-[#584738]/75 leading-relaxed mb-3">{text}</p>
    <div className="flex items-center gap-3 mb-3">
      <Stars rating={rating} />
      <span className="text-[11px] text-amber-500 font-sans font-semibold">{rating}.0</span>
    </div>
    {images.length > 0 && (
      <div className="flex gap-2 mt-2">
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            onClick={() => onImageClick && onImageClick(src)}
            className="w-14 h-14 object-cover border border-[#584738]/10 cursor-zoom-in hover:opacity-85 transition-opacity duration-200 hover:scale-105 transform"
          />
        ))}
      </div>
    )}
  </div>
);

const ALL_REVIEWS_DATA = [];

export default function ProductPage({
  view, setView,
  isMenuOpen, setIsMenuOpen,
  isCartOpen, setIsCartOpen,
  isBookingOpen, setIsBookingOpen,
  favorites, setFavorites,
  cartItems, setCartItems,
  bookingStep, setBookingStep,
  bookingForm, setBookingForm,
  product,
  setSelectedCollectionId,
  currentUser,
  setCurrentUser,
  products = [],
}) {
  // ── ALL HOOKS DECLARED BEFORE EARLY RETURN ──────────────────────────────────
  const liveProduct = (products && product)
    ? (products.find(p => String(p.id) === String(product.id)) || product)
    : product;
  const liveStock = (liveProduct && typeof liveProduct.stock === 'number')
    ? liveProduct.stock
    : (product && typeof product.stock === 'number' ? product.stock : 10);
  const isOutOfStock = liveStock <= 0;

  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(isOutOfStock ? 0 : 1);
  const [activeTab, setActiveTab] = useState('description');
  const [wishlisted, setWishlisted] = useState(false);
  const [reviewsList, setReviewsList] = useState(ALL_REVIEWS_DATA);
  const [visibleReviewsCount, setVisibleReviewsCount] = useState(3);
  const [showWriteForm, setShowWriteForm] = useState(false);
  const [newReviewRating, setNewReviewRating] = useState(5);
  const [newReviewText, setNewReviewText] = useState('');
  const [newReviewName, setNewReviewName] = useState('');
  const [newReviewImages, setNewReviewImages] = useState([]);
  const [isUploadingReviewImages, setIsUploadingReviewImages] = useState(false);
  const [attachMockImage, setAttachMockImage] = useState(false);
  const [undoItem, setUndoItem] = useState(null);
  const [activeLightboxImage, setActiveLightboxImage] = useState(null);
  const undoTimerRef = useRef(null);

  useEffect(() => {
    if (currentUser && !newReviewName) {
      setNewReviewName(currentUser.displayName || currentUser.email?.split('@')[0] || '');
    }
  }, [currentUser, newReviewName]);

  // Scroll to top and reset state when product changes
  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImage(0);
    setQty(isOutOfStock ? 0 : 1);
    setActiveTab('description');
    setVisibleReviewsCount(3);
    setShowWriteForm(false);
    setNewReviewName('');
    setNewReviewImages([]);

    if (!product) return;

    const unsubReviews = subscribeReviews((allReviews) => {
      const productReviews = (allReviews || []).filter(
        r => (r.productId && String(r.productId) === String(product.id)) ||
             (r.product && r.product.toLowerCase() === product.name.toLowerCase())
      );
      if (productReviews.length > 0) {
        setReviewsList(productReviews);
      } else {
        const formattedMocks = ALL_REVIEWS_DATA.map(r => ({
          ...r,
          product: product.name,
          productId: product.id,
          status: 'Approved'
        }));
        setReviewsList(formattedMocks);
      }
    }, ALL_REVIEWS_DATA);

    return () => {
      if (unsubReviews) unsubReviews();
    };
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center font-sans text-[#584738]">
        <div className="text-center">
          <h2 className="text-2xl font-serif mb-4">No product selected</h2>
          <button onClick={() => setView('home')} className="bg-[#584738] text-white px-6 py-2.5 text-xs uppercase tracking-widest font-bold">Go to Homepage</button>
        </div>
      </div>
    );
  }

  // Dynamic gallery images — reads the 4 slots from product.images, falling back to primary image or placeholders
  const imagesArray = product.images || [];
  const galleryImages = [
    product.image || imagesArray[0] || '/new_arrival_2.jpg',
    imagesArray[1] || product.image || '/new_arrival_3.jpg',
    imagesArray[2] || product.image || '/blue_pottery_prod_1.jpg',
    imagesArray[3] || product.image || '/blue_pottery_prod_2.jpg',
  ];

  const handleReviewImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setIsUploadingReviewImages(true);
    const urls = [...newReviewImages];
    for (const file of files) {
      try {
        const url = await uploadImage(file);
        urls.push(url);
      } catch (err) {
        console.warn("Upload failed, fallback to local URL");
        urls.push(URL.createObjectURL(file));
      }
    }
    setNewReviewImages(urls);
    setIsUploadingReviewImages(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!newReviewText.trim()) return;
    const reviewItem = {
      product: product.name,
      productId: product.id,
      name: currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Client',
      date: 'Just now',
      rating: newReviewRating,
      text: newReviewText,
      images: newReviewImages,
      status: 'Approved'
    };
    try {
      await saveReview(reviewItem);
      setReviewsList(prev => [reviewItem, ...prev]);
      setVisibleReviewsCount(prev => prev + 1);
      setNewReviewText('');
      setNewReviewName('');
      setNewReviewImages([]);
      setNewReviewRating(5);
      setShowWriteForm(false);
    } catch (err) {
      alert("Failed to submit your review to the database.");
    }
  };

  const getSubtotal = () => cartItems.reduce((acc, item) => acc + item.price * item.qty, 0);

  const handleAddToCart = () => {
    if (isOutOfStock) {
      triggerStockLimitAlert(0);
      return;
    }
    const maxStock = liveStock;
    setCartItems(prev => {
      const existing = prev.find(item => String(item.id) === String(liveProduct.id));
      const currentQty = existing ? existing.qty : 0;
      if (currentQty + qty > maxStock) {
        triggerStockLimitAlert(maxStock);
        const available = Math.max(0, maxStock - currentQty);
        if (available === 0) return prev;
        if (existing) {
          return prev.map(item => String(item.id) === String(liveProduct.id) ? { ...item, stock: maxStock, qty: maxStock } : item);
        } else {
          return [...prev, { ...liveProduct, stock: maxStock, qty: Math.min(qty, maxStock) }];
        }
      }
      if (existing) return prev.map(item => String(item.id) === String(liveProduct.id) ? { ...item, stock: maxStock, qty: item.qty + qty } : item);
      return [...prev, { ...liveProduct, stock: maxStock, qty }];
    });
  };

  const handleQtyChange = (id, delta) => {
    setCartItems(prev => {
      const item = prev.find(i => String(i.id) === String(id));
      if (!item) return prev;
      if (item.qty + delta <= 0) {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoItem(item);
        undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
        return prev.filter(i => String(i.id) !== String(id));
      }
      const liveProd = (products || []).find(p => String(p.id) === String(id));
      const itemStock = (liveProd && typeof liveProd.stock === 'number')
        ? liveProd.stock
        : ((typeof item.stock === 'number') ? item.stock : 10);
      if (delta > 0 && item.qty + delta > itemStock) {
        triggerStockLimitAlert(itemStock);
        return prev.map(i => String(i.id) === String(id) ? { ...i, stock: itemStock, qty: itemStock } : i);
      }
      return prev.map(i => String(i.id) === String(id) ? { ...i, stock: itemStock, qty: i.qty + delta } : i);
    });
  };

  const removeItem = (id) => {
    const item = cartItems.find(i => i.id === id);
    if (item) {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoItem(item);
      undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
    }
    setCartItems(prev => prev.filter(i => i.id !== id));
  };

  const handleUndo = () => {
    if (undoItem) {
      setCartItems(prev => [...prev, { ...undoItem, qty: 1 }]);
      setUndoItem(null);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    }
  };

  const additionalInfo = [
    { label: 'Material', value: product.material || '925 Sterling Silver' },
    { label: 'Finish', value: product.finish || 'Handcrafted Heritage Polish' },
    { label: 'Weight', value: product.weight || 'Approximately 45–65 grams' },
    { label: 'Collection', value: (product.collection && product.collection !== 'None') ? product.collection : (product.category || 'Luxury Heirlooms') },
    { label: 'Origin', value: product.origin || 'Jaipur, Rajasthan, India' },
    { label: 'Care', value: product.care || 'Store in anti-tarnish pouch. Avoid direct contact with water & perfumes.' },
  ];

  const tabs = ['description', 'additional information', 'reviews'];

  return (
    <div className="bg-noise min-h-screen font-sans text-[#584738] flex flex-col antialiased">

      {/* ── HEADER ── */}
      <Header
        setView={setView}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setIsSearchOpen={() => {}}
        setIsAuthOpen={setIsAuthOpen}
        setIsCartOpen={setIsCartOpen}
        cartItems={cartItems}
      />

      {/* ── MAIN ── */}
      <main className="max-w-7xl mx-auto px-4 md:px-12 py-6 md:py-10 w-full flex-1">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[10.5px] font-sans tracking-widest uppercase text-[#584738]/45 mb-4 flex-wrap">
          <button onClick={() => setView('home')} className="hover:text-[#584738] transition-colors focus:outline-none">Home</button>
          <span>/</span>
          <span className="text-[#584738]/70">{product.category || 'Jewellery'}</span>
          <span>/</span>
          <span className="text-[#584738]/70 truncate max-w-[150px] md:max-w-[280px]">{product.name}</span>
        </div>

        {/* ── PRODUCT DETAIL GRID ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16 items-start mb-6">

          {/* LEFT: Gallery */}
          <div className="flex flex-col gap-4 md:sticky md:top-24 md:self-start">
            <div className="relative overflow-hidden bg-[#FAF8F5] border border-[#584738]/8 aspect-square sm:aspect-[4/5] flex items-center justify-center">
              {galleryImages.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`${product.name} - view ${i + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out ${
                    activeImage === i ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                />
              ))}
              {product.isNewArrival && (
                <span className="absolute top-4 left-4 bg-[#584738] text-[#F5E6D8] text-[9px] font-sans font-bold tracking-[0.2em] px-3 py-1.5 uppercase">
                  New Arrival
                </span>
              )}
            </div>
            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-2.5">
              {galleryImages.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-square overflow-hidden border-2 transition-all duration-200 focus:outline-none bg-[#FAF8F5] flex items-center justify-center ${activeImage === i ? 'border-[#584738]' : 'border-transparent hover:border-[#584738]/40'}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* RIGHT: Product Info */}
          <div className="flex flex-col gap-5 pt-2">

            <h2 className="font-serif text-2xl md:text-3xl font-normal tracking-wide text-[#584738] leading-snug">
              {product.name}
            </h2>

            <div className="flex items-center gap-3">
              <Stars rating={5} />
              <span className="text-[12px] font-sans text-[#584738]/55 tracking-wide">
                5.0 ({reviewsList.length} Customer Reviews)
              </span>
            </div>

            {isOutOfStock ? (
              <div className="flex items-center gap-2.5">
                <span className="inline-block px-3 py-1 bg-[#8C3E3E] text-white text-[10px] font-sans font-bold tracking-widest uppercase">
                  Out of Stock
                </span>
                <span className="text-[11.5px] text-[#8C3E3E] font-sans font-medium">
                  Currently unavailable — check back soon
                </span>
              </div>
            ) : liveStock <= 5 ? (
              <span className="text-[11.5px] text-[#8C3E3E] font-medium font-sans">
                Only {liveStock} left in stock — order soon
              </span>
            ) : null}

            <div className="flex items-baseline gap-2.5 flex-wrap">
              <p className="text-xl font-sans font-bold text-[#584738] tracking-wide">
                Rs. {product.price.toLocaleString('en-IN')}.00
              </p>
              <span className="text-[11.5px] text-[#584738]/60 font-sans font-normal tracking-wide">
                (Inclusive of all taxes)
              </span>
            </div>

            <p className="text-[12px] font-sans text-[#584738]/60 border-l-2 border-[#584738]/30 pl-3 leading-relaxed">
              Made with 925 Silver &nbsp;||&nbsp; Well Stamped with 925
            </p>

            <div className="h-px bg-[#584738]/10" />

            <div
              className="text-[13px] text-[#584738]/70 leading-relaxed font-sans"
              dangerouslySetInnerHTML={{
                __html: product.description || 'Each piece of House of Karnnikaraa collection is custom designed and carefully detailed by the master karigars of Jaipur, merging the enduring history of Indian craft with lightweight, clean, and modern styling.'
              }}
            />

            {/* Qty + Add to Bag */}
            <div className="flex items-stretch gap-3">
              <div className={`flex items-center border border-[#584738]/30 select-none ${isOutOfStock ? 'opacity-40 pointer-events-none' : ''}`}>
                <button 
                  disabled={isOutOfStock || qty <= 1} 
                  onClick={() => setQty(q => Math.max(1, q - 1))} 
                  className="w-10 h-full flex items-center justify-center text-lg text-[#584738] hover:bg-[#584738]/5 transition-colors focus:outline-none disabled:opacity-40"
                >
                  -
                </button>
                <span className="w-10 text-center font-sans font-semibold text-[#584738] text-sm">{isOutOfStock ? 0 : qty}</span>
                <button
                  disabled={isOutOfStock}
                  onClick={() => {
                    const maxStock = liveStock;
                    setQty(q => {
                      if (q + 1 > maxStock) {
                        triggerStockLimitAlert(maxStock);
                        return maxStock;
                      }
                      return q + 1;
                    });
                  }}
                  className="w-10 h-full flex items-center justify-center text-lg text-[#584738] hover:bg-[#584738]/5 transition-colors focus:outline-none disabled:opacity-40"
                >
                  +
                </button>
              </div>
              {isOutOfStock ? (
                <button
                  disabled={true}
                  className="flex-1 bg-neutral-300 text-neutral-500 py-3.5 text-xs font-sans font-bold tracking-widest uppercase cursor-not-allowed shadow-xs"
                >
                  Out of Stock
                </button>
              ) : (
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-[#CEC1A8] hover:bg-[#b8a990] text-[#584738] py-3.5 text-xs font-sans font-bold tracking-widest uppercase transition-colors shadow-xs"
                >
                  Add to Bag
                </button>
              )}
            </div>

            {/* Buy It Now */}
            {isOutOfStock ? (
              <button
                disabled={true}
                className="w-full bg-neutral-200 text-neutral-400 py-3.5 text-xs font-sans font-bold tracking-widest uppercase cursor-not-allowed shadow-xs"
              >
                Out of Stock
              </button>
            ) : (
              <button
                onClick={() => { handleAddToCart(); setIsCartOpen(false); setView('checkout'); }}
                className="w-full bg-[#584738] text-white hover:bg-black py-3.5 text-xs font-sans font-bold tracking-widest uppercase transition-colors shadow-xs"
              >
                Buy It Now
              </button>
            )}

            {/* Wishlist */}
            <button
              onClick={() => setWishlisted(w => !w)}
              className="flex items-center gap-2 text-[12px] font-sans text-[#584738]/65 hover:text-[#584738] transition-colors focus:outline-none self-start"
            >
              <HeartIcon filled={wishlisted} />
              {wishlisted ? 'Saved to Wishlist' : 'Add to Wishlist'}
            </button>

            <div className="h-px bg-[#584738]/10" />

            {/* Trust block */}
            <div className="bg-[#584738]/[0.03] border border-[#584738]/8 p-4 rounded-xs">
              <p className="text-[12px] font-serif font-medium text-[#584738] mb-1 leading-relaxed">
                Trust matters when buying Fine Jewellery.<br />
                <span className="font-normal text-[#584738]/65">Let us bring the experience to you in the comfort of your home.</span>
              </p>
            </div>

            {/* Support links */}
            <div className="flex flex-col gap-2.5">
              <a href="tel:+919166209215" className="text-[11.5px] font-sans text-[#584738]/60 hover:text-[#584738] flex items-center gap-2 transition-colors">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
                Contact us +91 91662 09215
              </a>
              <button onClick={() => setIsBookingOpen(true)} className="text-[11.5px] font-sans text-[#584738]/60 hover:text-[#584738] flex items-center gap-2 transition-colors focus:outline-none text-left">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
                Book a Store Visit
              </button>
            </div>
          </div>
        </div>

        {/* ── DETAILS TABS ── */}
        <div className="mt-12 md:mt-20 border-t border-[#584738]/10 pt-10">
          <div className="flex justify-center border-b border-[#584738]/10 mb-8 overflow-x-auto scrollbar-none gap-8">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 text-xs md:text-sm font-sans tracking-widest uppercase focus:outline-none transition-colors duration-300 border-b-2 whitespace-nowrap font-semibold ${
                  activeTab === tab 
                    ? 'border-[#584738] text-[#584738]' 
                    : 'border-transparent text-[#584738]/40 hover:text-[#584738]/80'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="min-h-[150px] font-sans">
            {activeTab === 'description' && (
              <div className="text-sm text-[#584738]/75 leading-relaxed max-w-4xl whitespace-pre-line">
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: product.description || 'No description available for this item.' 
                  }} 
                />
              </div>
            )}

            {activeTab === 'additional information' && (
              <div className="max-w-2xl border border-[#584738]/10 rounded-xs overflow-hidden">
                {additionalInfo.map((info, idx) => (
                  <div key={idx} className={`flex border-b border-[#584738]/5 text-xs py-3 px-4 ${idx % 2 === 0 ? 'bg-[#584738]/[0.015]' : ''}`}>
                    <span className="w-1/3 font-bold text-[#584738] uppercase tracking-wider">{info.label}</span>
                    <span className="w-2/3 text-[#584738]/70">{info.value}</span>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="max-w-3xl">
                <h3 className="font-serif text-lg md:text-xl font-medium mb-6">Customer Reviews</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start mb-8 bg-[#584738]/[0.02] border border-[#584738]/10 p-6 rounded-xs">
                  <div>
                    <p className="text-3xl font-sans font-bold text-[#584738] mb-1">4.9</p>
                    <div className="flex items-center gap-2 mb-2">
                      <Stars rating={5} />
                      <span className="text-xs text-[#584738]/60 font-sans">Based on {reviewsList.length} reviews</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <RatingBar label="5 Stars" pct={92} />
                    <RatingBar label="4 Stars" pct={6} />
                    <RatingBar label="3 Stars" pct={2} />
                    <RatingBar label="2 Stars" pct={0} />
                    <RatingBar label="1 Star" pct={0} />
                  </div>
                </div>

                <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#584738]/10 flex-wrap gap-3">
                  <button
                    onClick={() => {
                      if (!currentUser) {
                        alert('Please sign in to write a review.');
                        setIsAuthOpen(true);
                      } else {
                        setShowWriteForm(prev => !prev);
                      }
                    }}
                    className="border border-[#584738] hover:bg-[#584738] hover:text-white px-5 py-2.5 text-xs font-sans tracking-wider uppercase font-semibold transition-colors"
                  >
                    {showWriteForm ? 'Cancel Review' : 'Write A Review'}
                  </button>
                </div>

                {showWriteForm && (
                  <form onSubmit={handleFormSubmit} className="bg-[#FAF8F5] border border-[#584738]/10 p-6 rounded-xs mb-8 flex flex-col gap-4 font-sans text-left animate-fade-in w-full">
                    <h4 className="font-serif font-semibold text-[#584738]">Write your review</h4>
                    
                    <div className="flex items-center gap-3">
                      <span className="text-xs uppercase text-[#584738]/60 tracking-wider">Your Rating:</span>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(val => (
                          <button key={val} type="button" onClick={() => setNewReviewRating(val)} className="focus:outline-none cursor-pointer p-0.5 hover:scale-110 transition-transform duration-200">
                            <svg className={`w-6 h-6 transition-colors duration-200 ${val <= newReviewRating ? 'text-amber-500' : 'text-[#584738]/20'}`} fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] uppercase text-[#584738]/60 tracking-wider mb-1">Your Name:</label>
                      <input 
                        type="text" 
                        value={currentUser ? (currentUser.displayName || currentUser.email?.split('@')[0] || 'Client') : ''}
                        disabled
                        className="w-full bg-transparent border border-[#584738]/20 p-3 text-sm text-[#584738]/60 cursor-not-allowed select-none outline-none font-medium rounded-xs" 
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] uppercase text-[#584738]/60 tracking-wider mb-1">Review Details:</label>
                      <textarea value={newReviewText} onChange={e => setNewReviewText(e.target.value)} required rows="4" className="w-full bg-white border border-[#584738]/25 p-3 text-sm focus:outline-none focus:border-[#584738] rounded-xs" placeholder="What did you like or dislike about this design?"></textarea>
                    </div>

                    <div>
                      <label className="block text-[11px] uppercase text-[#584738]/60 tracking-wider mb-1">Upload styling photos (optional):</label>
                      <label className="flex items-center justify-center border border-dashed border-[#584738]/25 hover:border-[#584738] bg-white py-3 text-center text-xs text-[#584738] font-bold uppercase tracking-wider cursor-pointer transition-colors duration-200 rounded-xs">
                        <span>{isUploadingReviewImages ? 'Uploading photos...' : 'Choose styling images'}</span>
                        <input 
                          type="file" 
                          multiple 
                          accept="image/*" 
                          disabled={isUploadingReviewImages}
                          onChange={handleReviewImageUpload} 
                          className="hidden" 
                        />
                      </label>
                      {newReviewImages.length > 0 && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                          {newReviewImages.map((img, i) => (
                            <img key={i} src={img} alt="review upload" className="w-12 h-12 object-cover border border-[#584738]/10" />
                          ))}
                        </div>
                      )}
                    </div>

                    <button type="submit" disabled={isUploadingReviewImages} className="bg-[#584738] hover:bg-black disabled:opacity-50 text-white py-3.5 text-xs tracking-widest font-bold uppercase rounded-xs transition-colors self-start px-8">Submit Review</button>
                  </form>
                )}

                <div>
                  {reviewsList.slice(0, visibleReviewsCount).map((r, i) => (
                    <ReviewCard key={i} {...r} onImageClick={setActiveLightboxImage} />
                  ))}
                </div>

                {visibleReviewsCount < reviewsList.length && (
                  <button
                    onClick={() => setVisibleReviewsCount(prev => prev + 3)}
                    className="w-full py-4 text-xs font-sans uppercase font-bold tracking-widest text-[#584738] hover:text-black border border-[#584738]/10 hover:border-[#584738]/40 transition-colors mt-6"
                  >
                    Load More Reviews
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <Footer setView={setView} setIsBookingOpen={setIsBookingOpen} />

      {/* DRAWERS */}
      <MenuDrawer
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        setView={setView}
        setIsBookingOpen={setIsBookingOpen}
        setSelectedCollectionId={setSelectedCollectionId}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        setIsAuthOpen={setIsAuthOpen}
      />
      <BookingDrawer
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        bookingStep={bookingStep}
        setBookingStep={setBookingStep}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
        cartItems={cartItems}
      />
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} currentUser={currentUser} setCurrentUser={setCurrentUser} />

      {/* CART SLIDE-OVER */}
      <div className={`fixed inset-0 z-50 transition-opacity duration-300 pointer-events-none ${isCartOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0'}`}>
        <div onClick={() => setIsCartOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-xs" />
        <div className={`fixed inset-y-0 right-0 h-full w-full md:w-96 bg-[#FAF8F5] shadow-2xl p-6 transition-transform duration-300 flex flex-col ${isCartOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between border-b border-[#584738]/10 pb-4 mb-6 flex-shrink-0">
            <h3 className="font-serif text-lg tracking-widest font-semibold uppercase flex items-center gap-2 text-[#584738]">
              Your Cart
              <span className="text-xs bg-[#584738]/10 text-[#584738] px-2.5 py-0.5 rounded-full font-sans font-bold">
                {cartItems.reduce((acc, it) => acc + it.qty, 0)}
              </span>
            </h3>
            <button onClick={() => setIsCartOpen(false)} className="text-[#584738] hover:opacity-75 focus:outline-none">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1">
            {cartItems.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#584738]/40 font-serif">
                <p className="mt-3 text-sm tracking-wide font-medium">Your cart is empty.</p>
              </div>
            ) : (
              cartItems.map((item) => (
                <div key={item.id} className="flex gap-4 pb-5 border-b border-[#584738]/5 items-start">
                  <div className="w-16 h-16 rounded-xs bg-gray-50 overflow-hidden flex-shrink-0 border border-[#584738]/10">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif text-[13.5px] font-semibold text-[#584738] truncate">{item.name}</h4>
                    <p className="text-[12.5px] text-[#584738] font-medium mt-1">
                      ₹{item.price.toLocaleString('en-IN')} <span className="text-[9.5px] text-[#584738]/60 font-sans font-normal">(incl. of all taxes)</span>
                    </p>
                    <div className="flex items-center justify-between text-xs mt-2 font-sans">
                      <div className="flex items-center border border-[#584738]/20 select-none">
                        <button onClick={() => handleQtyChange(item.id, -1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none">-</button>
                        <span className="px-3 py-0.5 text-center font-medium">{item.qty}</span>
                        <button onClick={() => handleQtyChange(item.id, 1)} className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none">+</button>
                      </div>
                      <button onClick={() => removeItem(item.id)} className="text-[#584738]/60 hover:text-red-500 font-semibold">Remove</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {cartItems.length > 0 && (
            <div className="border-t border-[#584738]/10 pt-6 mt-4 flex flex-col gap-4 flex-shrink-0">
              <div className="flex items-center justify-between font-serif">
                <div className="flex flex-col text-left">
                  <span className="font-medium tracking-wide text-[#584738]/80">Subtotal</span>
                  <span className="text-[10px] text-[#584738]/60 font-sans normal-case">(Inclusive of all taxes)</span>
                </div>
                <span className="text-lg font-bold text-[#584738]">₹{getSubtotal().toLocaleString('en-IN')}</span>
              </div>
              <p className="text-[10px] text-[#584738]/60 tracking-wider text-center font-sans">Complimentary Express Delivery • (Inclusive of all taxes)</p>
              <button
                onClick={() => { setView('checkout'); setIsCartOpen(false); }}
                className="w-full bg-[#584738] text-white hover:bg-black py-3.5 text-xs tracking-widest font-bold uppercase transition-colors"
              >
                PROCEED TO CHECKOUT
              </button>
            </div>
          )}

          {undoItem && (
            <div className="flex-shrink-0 mt-3 flex items-center justify-between gap-3 bg-[#584738] text-[#F5E6D8] px-4 py-3 rounded-xs">
              <p className="text-[11.5px] font-sans tracking-wide truncate flex-1">
                <span className="opacity-60 mr-1">Removed:</span>{undoItem.name}
              </p>
              <button onClick={handleUndo} className="text-[11px] font-sans font-bold tracking-widest uppercase text-[#CEC1A8] hover:text-white transition-colors focus:outline-none flex-shrink-0">
                Undo
              </button>
            </div>
          )}
        </div>
      </div>

      {/* LIGHTBOX */}
      {activeLightboxImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" onClick={() => setActiveLightboxImage(null)}>
          <img src={activeLightboxImage} alt="" className="max-w-full max-h-[90vh] object-contain border border-white/10 shadow-2xl" onClick={e => e.stopPropagation()} />
          <button onClick={() => setActiveLightboxImage(null)} className="absolute top-4 right-4 text-white hover:opacity-75 focus:outline-none">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
